import Dashboard from "components/Template/Dashboard";
import React, { useEffect, useState } from "react";
import ArrowLeft from "icons/arrow-circle-left-green.svg";
import PasswordInputAuth from "components/PasswordInputAuth";
import Loading from "components/Loading";
import Confirm from "icons/confirm-circle-white.svg";
import Button from "components/Button";
import { schemaChangePassword } from "./validation";
import handleErrors from "helpers/handleErrors";
import ValidationPassword from "components/ValidationPassword";
import TextInputAuth from "components/TextInputAuth";
import { ToastContainer } from "react-toastify";
import { useHistory } from "react-router-dom";
import { validateCode } from "services/auth";
import { changePassword as passwordChange } from "services/auth";
import toastMessage from "helpers/handleToastMessage";
import jwt_decode from "jwt-decode";

type Props = {};

const ChangePassword: React.FC<Props> = (): JSX.Element => {
  let history = useHistory();

  const [isHidden, setIsHidden] = useState(false);
  const [receivedCode, setReceivedCode] = useState("");
  const [stepOk, setStepOk] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [changePassword, setChangePassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>();
  const [state, setState] = useState("");
  const [newState, setNewState] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  const [isValidReceivedCode, setIsValidReceivedCode] = useState(false);
  const [isValidMin8Characters, setIsValidMin8Characters] =
    useState<boolean>(false);
  const [isValidCharacters, setIsValidCharacters] = useState<boolean>(false);
  const [isValidSamePassword, setIsValidSamePassword] =
    useState<boolean>(false);

  useEffect(() => {
    setState(history.location.state.changeState);
    setIsHidden(hidden(newPassword, changePassword));

    const token = localStorage.getItem("token");
    if (token) {
      let decoded: any = jwt_decode(token);
      setEmail(decoded.email);
      setRole(decoded.role);
    }

    const validReceivedCode = (code: string): boolean => {
      if (code === "") return false;
      if (code.length === 6) return true;
      return false;
    };

    const validMin8Characters = (password: string): boolean => {
      if (password.trim() === "") return false;
      return password.length >= 8;
    };

    const validNumericLetters = (password: string): boolean => {
      if (password.trim() === "") return false;
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(password)) return false;
      return true;
    };

    const validSamePassword = (password: string, confirm: string): boolean => {
      if (password.trim() === "" && confirm.trim() === "") return true;
      if (password !== confirm) return false;
      return true;
    };

    const isValidCode = validReceivedCode(receivedCode);
    const isValidMin8Characters = validMin8Characters(newPassword);
    const isValidCharacters = validNumericLetters(newPassword);
    const isValidSamePassword = validSamePassword(newPassword, changePassword);

    setIsValidReceivedCode(isValidCode);
    setIsValidMin8Characters(isValidMin8Characters);
    setIsValidCharacters(isValidCharacters);
    setIsValidSamePassword(isValidSamePassword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePassword, newPassword, receivedCode]);

  const hidden = (password: string, confirm: string): boolean => {
    if (password.trim() === "" && confirm.trim() === "") return true;
    return false;
  };

  const submitValidateCode = async () => {
    try {
      setLoading(true);
      const { data } = await validateCode({
        email,
        code: receivedCode,
        state,
        role,
      });

      if (data.message === "Step okay!") {
        setNewState(data.state);
        setStepOk(true);
      }
    } catch (error: any) {
      toastMessage({
        message: "Error, when trying to valid code.",
        type: "error",
      });
      history.goBack();
    } finally {
      setLoading(false);
    }
  };

  const addClassButton = () => {
    if (errors?.password || errors?.newPassword) {
      return "bg-bt-red text-white";
    }

    if (isValidMin8Characters && isValidCharacters && isValidSamePassword) {
      return "bg-bt-green text-white";
    }

    return "bg-bt-light-gray text-black";
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (
        !schemaChangePassword.isValidSync({
          newPassword,
          changePassword,
        })
      ) {
        handleErrors({
          schema: schemaChangePassword,
          stateData: {
            newPassword,
            changePassword,
          },
          setErrorMessage: setErrors,
        });
        setLoading(false);
        return;
      }

      const { data } = await passwordChange({
        email,
        password: newPassword,
        state: newState,
        role,
      });

      toastMessage({
        message: "Change password successfully.",
        type: "success",
      });

      if (data) {
        if (role === "admin") {
          history.push("/login/admin");
        } else {
          history.push("/login/entity");
        }
        localStorage.clear();
        window.location.reload();
      }
    } catch (error: any) {
      toastMessage({
        message: "Error, when trying to change password.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dashboard
      breadcrumb={[
        { name: "Account Settings", active: false, path: "/account-settings" },
        { name: "Change Password", active: true },
      ]}
      icon={ArrowLeft}
      title={"Change Password"}
      children={
        <div className="bg-bt-white h-96 min-h-desktop-message p-4">
          <Loading loading={loading} />
          <div className="w-9/12 mx-auto mt-4">
            <div className="flex flex-row mt-2">
              <TextInputAuth
                label="Received Code"
                placeholder={"Received Code"}
                value={receivedCode}
                onChange={(e: any) => setReceivedCode(e.target.value)}
                className={"w-3/12"}
                isHelp={errors?.receivedCode}
                helpText={errors?.receivedCode}
                onBlur={submitValidateCode}
                maxLength={6}
                minLength={6}
              />
            </div>
            {receivedCode && isValidReceivedCode && stepOk && (
              <div>
                <div className="flex flex-row mt-2">
                  <PasswordInputAuth
                    label="New Password"
                    placeholder={"New Password"}
                    value={newPassword}
                    onChange={(e: any) => setNewPassword(e.target.value)}
                    className={"w-8/12"}
                    isHelp={errors?.newPassword}
                    helpText={errors?.newPassword}
                  />
                </div>
                <div className="justify-center items-center mt-4">
                  <ValidationPassword
                    isHidden={isHidden}
                    password={newPassword}
                    passwordConfirm={changePassword}
                  />
                </div>
                <div className="flex flex-row mt-2">
                  <PasswordInputAuth
                    label="Re-type New Password"
                    placeholder={"Re-type New Password"}
                    value={changePassword}
                    onChange={(e: any) => setChangePassword(e.target.value)}
                    className={"w-8/12"}
                    isHelp={errors?.changePassword}
                    helpText={errors?.changePassword}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-row justify-end">
            <Button
              label={"Save Changes"}
              iconLeft={Confirm}
              className={`h-14 w-48 font-sora font-medium2 text-base text-bt-white bg-bt-green rounded-full ${addClassButton()}`}
              onClick={handleSubmit}
            />
          </div>
          <ToastContainer />
        </div>
      }
    />
  );
};

export default ChangePassword;
